import { useState, useEffect } from 'react';

import { Basket } from '../types/basket.type';

type BasketReference = { id: string; name: string };

const EMPTY_REFERENCE: BasketReference = { id: '', name: '' };

type UseBasketEditFormResult = {
  isEditing: boolean;
  updatedBasket: Basket;
  handleFieldChange: ({ field, value }: { field: keyof Basket; value: unknown }) => void;
  handleCategoryChange: (category: BasketReference | null) => void;
  handleSubCategoryChange: (subCategory: BasketReference | null) => void;
  handleCancelEditing: () => void;
  handleSave: (updateFn: (editedFields: Partial<Basket>) => Promise<void>) => Promise<void>;
};

export function useBasketEditForm(basket: Basket): UseBasketEditFormResult {
  const [isEditing, setIsEditing] = useState(false);
  const [updatedBasket, setUpdatedBasket] = useState<Basket>(basket);
  const [editedFields, setEditedFields] = useState<Partial<Basket>>({});

  useEffect(() => {
    setUpdatedBasket(basket);
    setEditedFields({});
    setIsEditing(false);
  }, [basket]);

  const handleFieldChange = ({ field, value }: { field: keyof Basket; value: unknown }) => {
    setIsEditing(true);
    setUpdatedBasket((previous) => ({ ...previous, [field]: value }));
    setEditedFields((previous) => ({ ...previous, [field]: value }));
  };

  const handleCategoryChange = (category: BasketReference | null) => {
    setIsEditing(true);
    setUpdatedBasket((previous) => ({
      ...previous,
      category: category ?? EMPTY_REFERENCE,
      subCategory: EMPTY_REFERENCE,
    }));
    setEditedFields((previous) => {
      const next: Partial<Basket> = { ...previous, category: category ?? EMPTY_REFERENCE };
      delete next.subCategory;
      return next;
    });
  };

  const handleSubCategoryChange = (subCategory: BasketReference | null) => {
    setIsEditing(true);
    setUpdatedBasket((previous) => ({ ...previous, subCategory: subCategory ?? EMPTY_REFERENCE }));
    setEditedFields((previous) => ({ ...previous, subCategory: subCategory ?? EMPTY_REFERENCE }));
  };

  const handleCancelEditing = () => {
    setUpdatedBasket(basket);
    setEditedFields({});
    setIsEditing(false);
  };

  const handleSave = async (updateFn: (editedFields: Partial<Basket>) => Promise<void>) => {
    if (Object.keys(editedFields).length === 0) return;
    await updateFn(editedFields);
  };

  return {
    isEditing,
    updatedBasket,
    handleFieldChange,
    handleCategoryChange,
    handleSubCategoryChange,
    handleCancelEditing,
    handleSave,
  };
}
